import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Grid,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  EmojiEvents as TrophyIcon,
  Videocam as VideoIcon,
  TrendingUp as TrendingUpIcon,
  Bolt as BoltIcon,
  CheckCircle as CheckIcon,
  Stars as StarsIcon,
  People as PeopleIcon,
  PlayCircle as PlayIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useHistory } from 'react-router-dom';
import notifier from '../../components/Notifier';
import ApiService from '../../services/api-service';
import SEO from '../../components/SEO';

export default function PowerPlaysInfo() {
  const theme = useTheme();
  const history = useHistory();
  const [formData, setFormData] = useState({
    brandName: '',
    contactEmail: '',
    prizeType: '',
    prizeValue: '',
    campaignGoals: [],
    targetAudience: '',
    startDate: '',
    guestRequests: '',
  });

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmitSponsorRequest = async () => {
    if (!formData.brandName || !formData.contactEmail || !formData.prizeType) {
      notifier.error('Please fill in required fields');
      return;
    }

    try {
      const response = await ApiService.post('/public/powerplays/sponsor-request', formData);
      console.log('Sponsor request response:', response);

      if (response && response.ok) {
        notifier.success('Sponsor request submitted! We\'ll be in touch soon.');
        setFormData({
          brandName: '',
          contactEmail: '',
          prizeType: '',
          prizeValue: '',
          campaignGoals: [],
          targetAudience: '',
          startDate: '',
          guestRequests: '',
        });
      } else {
        notifier.error(response?.message || 'Failed to submit request');
      }
    } catch (error) {
      console.error('Error submitting sponsor request:', error);
      notifier.error(error.message || 'Failed to submit request. Please try again.');
    }
  };

  const examplePowerPlays = [
    {
      title: 'Breakout Single Challenge',
      prize: '$5,000 cash + playlist placement',
      goals: 'SIKI Pro + tip voting',
      rounds: '4 rounds',
      reach: '10,000+ views',
    },
    {
      title: 'Brand Drop Showcase',
      prize: '$10,000 product + creator deal',
      goals: 'Product purchase + follows',
      rounds: '5 rounds',
      reach: '25,000+ views',
    },
    {
      title: 'Streamer Spotlight Cup',
      prize: 'Sponsor package + paid collab',
      goals: 'Channel subs + UGC clips',
      rounds: '3 rounds',
      reach: '15,000+ views',
    },
  ];

  const faqs = [
    {
      question: 'How is the winner chosen?',
      answer: 'Winners are selected through a combination of judge scoring and weighted audience engagement. We can customize the scoring breakdown to prevent pay-to-win scenarios while maintaining credibility.',
    },
    {
      question: 'How do you prevent pay-to-win?',
      answer: 'We implement caps on tip voting, split scoring between skill and engagement, and use verification systems to ensure fair competition while still driving sponsor goals.',
    },
    {
      question: 'What does SIKI Pro include?',
      answer: 'SIKI Pro includes unlimited streams, free uploads, priority playlist submission, 90% tips and 80% subscription revenue, instant withdrawals, and exclusive access to PowerPlays.',
    },
    {
      question: 'What does the sponsor need to provide?',
      answer: 'Sponsors provide the prize (cash/product/opportunity), brand assets for promotion, campaign goals, and approval for judges/hosts. We handle all production and execution.',
    },
    {
      question: 'How long does it take to launch?',
      answer: 'Typically 2-4 weeks from agreement to launch, depending on prize complexity and scheduling with judges/guests.',
    },
    {
      question: 'What are the content rights?',
      answer: 'Content usage terms are negotiated per sponsor. Generally, both SIKI and the sponsor can use highlight clips for promotional purposes, with specific terms outlined in the agreement.',
    },
  ];

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.mode === 'dark' ? '#0a0a0a' : '#fafafa',
        minHeight: '100vh',
        color: theme.palette.text.primary,
      }}
    >
      <SEO
        title="PowerPlays — Get Featured & Grow Your Music Career"
        description="Submit your music, get featured on SIKI playlists, and connect with the music industry. PowerPlays give independent artists the exposure they need to grow."
        path="/powerplays"
      />
      {/* Hero Section */}
      <Box
        sx={{
          position: 'relative',
          minHeight: '85vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: theme.palette.background.default,
          borderBottom: `1px solid ${theme.palette.divider}`,
          overflow: 'hidden',
        }}
      >

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, textAlign: 'center', px: 3 }}>
          <BoltIcon
            sx={{
              fontSize: 80,
              color: theme.palette.primary.main,
              mb: 3,
              filter: 'drop-shadow(0 0 20px currentColor)',
            }}
          />

          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: '2.5rem', md: '4rem' },
              fontWeight: 900,
              mb: 2,
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              letterSpacing: '-0.02em',
            }}
          >
            SIKI Power Plays
          </Typography>

          <Typography
            variant="h5"
            sx={{
              fontSize: { xs: '1.1rem', md: '1.5rem' },
              color: theme.palette.text.secondary,
              mb: 1,
              maxWidth: 800,
              mx: 'auto',
              lineHeight: 1.6,
            }}
          >
            Sponsor a live competition that turns artists & livestreamers into customers, creators, and fans—on camera.
          </Typography>

          <Typography
            variant="body1"
            sx={{
              fontSize: { xs: '0.95rem', md: '1.1rem' },
              color: theme.palette.text.secondary,
              mb: 5,
              maxWidth: 900,
              mx: 'auto',
              lineHeight: 1.7,
            }}
          >
            You provide the prize (cash or promotional opportunity). SIKI runs the tournament in livestream rounds with special guests, real audience momentum, and measurable outcomes.
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap', mb: 5 }}>
            <Button
              variant="contained"
              size="large"
              onClick={() => {
                document.getElementById('sponsor-form')?.scrollIntoView({ behavior: 'smooth' });
              }}
              sx={{
                px: 5,
                py: 2,
                fontSize: '1.1rem',
                fontWeight: 700,
                textTransform: 'none',
                borderRadius: 3,
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                boxShadow: `0 8px 24px ${theme.palette.primary.main}40`,
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: `0 12px 32px ${theme.palette.primary.main}60`,
                },
                transition: 'all 0.3s',
              }}
            >
              Become a Sponsor
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => {
                document.getElementById('examples')?.scrollIntoView({ behavior: 'smooth' });
              }}
              sx={{
                px: 5,
                py: 2,
                fontSize: '1.1rem',
                fontWeight: 700,
                textTransform: 'none',
                borderRadius: 3,
                borderWidth: 2,
                '&:hover': {
                  borderWidth: 2,
                  transform: 'translateY(-2px)',
                },
                transition: 'all 0.3s',
              }}
            >
              See Example Campaigns
            </Button>
          </Box>

          {/* Quick Bullets */}
          <Grid container spacing={2} sx={{ maxWidth: 900, mx: 'auto' }}>
            {[
              'Prize-backed: cash, placements, gear, studio time, distribution, or brand promos',
              'Live rounds: bracket-style eliminations hosted on SIKI with guest judges',
              'Conversion goals: SIKI Pro subs + optional product purchase / channel follow / tips-for-votes',
            ].map((text, idx) => (
              <Grid item xs={12} md={4} key={idx}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                  <CheckIcon sx={{ color: theme.palette.primary.main, mt: 0.5, flexShrink: 0 }} />
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.6 }}>
                    {text}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* What is a Power Play */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Typography
          variant="h3"
          sx={{
            fontWeight: 800,
            textAlign: 'center',
            mb: 3,
            fontSize: { xs: '2rem', md: '2.5rem' },
          }}
        >
          What is a Power Play?
        </Typography>
        <Typography
          variant="body1"
          sx={{
            textAlign: 'center',
            color: theme.palette.text.secondary,
            mb: 6,
            maxWidth: 800,
            mx: 'auto',
            lineHeight: 1.8,
            fontSize: '1.1rem',
          }}
        >
          A Power Play is a sponsor-backed live competition where creators compete for a prize through multiple livestream rounds. Judges and special guests review submissions live, advance finalists, and create viral, clip-worthy moments that drive traffic back to your brand.
        </Typography>

        <Grid container spacing={4}>
          {[
            {
              icon: <TrophyIcon sx={{ fontSize: 50 }} />,
              title: 'You Fund the Prize',
              description: 'Cash prize or promotional opportunity (placement, collab, brand deal, distribution, gear)',
            },
            {
              icon: <VideoIcon sx={{ fontSize: 50 }} />,
              title: 'We Run the Rounds Live',
              description: 'Hosted livestreams, bracket progression, guest judges, highlight clips',
            },
            {
              icon: <TrendingUpIcon sx={{ fontSize: 50 }} />,
              title: 'You Get Measurable Outcomes',
              description: 'Subscriber growth, product conversions, follows, tips/engagement, UGC + content rights (as agreed)',
            },
          ].map((card, idx) => (
            <Grid item xs={12} md={4} key={idx}>
              <Card
                sx={{
                  p: 4,
                  height: '100%',
                  textAlign: 'center',
                  background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 3,
                  transition: 'all 0.3s',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    borderColor: theme.palette.primary.main,
                    boxShadow: `0 12px 32px ${theme.palette.primary.main}20`,
                  },
                }}
              >
                <Box sx={{ color: theme.palette.primary.main, mb: 2 }}>{card.icon}</Box>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                  {card.title}
                </Typography>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.7 }}>
                  {card.description}
                </Typography>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Sponsor Options */}
      <Box sx={{ backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)', py: 10 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" sx={{ fontWeight: 800, textAlign: 'center', mb: 6, fontSize: { xs: '2rem', md: '2.5rem' } }}>
            Choose Your Prize Type
          </Typography>

          <Grid container spacing={3}>
            {[
              { title: 'Cash Prize', range: 'e.g., $2,500–$25,000', icon: '💰' },
              { title: 'Promotional Opportunity', range: 'brand placement, sponsored content package, campaign slot', icon: '📢' },
              { title: 'Career Opportunity', range: 'studio time, distribution deal, mentorship, collab session', icon: '🎯' },
              { title: 'Product Prize', range: 'gear drop, merch bundles, limited edition items', icon: '🎁' },
              { title: 'Hybrid', range: 'cash + promo + product', icon: '⚡' },
            ].map((option, idx) => (
              <Grid item xs={12} sm={6} md={4} key={idx}>
                <Card
                  sx={{
                    p: 3,
                    height: '100%',
                    background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'white',
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 2,
                    transition: 'all 0.2s',
                    '&:hover': {
                      borderColor: theme.palette.primary.main,
                      transform: 'translateY(-4px)',
                    },
                  }}
                >
                  <Typography variant="h2" sx={{ mb: 2 }}>
                    {option.icon}
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                    {option.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                    {option.range}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Typography
            variant="caption"
            sx={{
              display: 'block',
              textAlign: 'center',
              mt: 4,
              color: theme.palette.text.secondary,
              fontStyle: 'italic',
            }}
          >
            Prize size impacts number of applicants, media traction, and recommended number of rounds.
          </Typography>
        </Container>
      </Box>

      {/* How It Works Timeline */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Typography variant="h3" sx={{ fontWeight: 800, textAlign: 'center', mb: 6, fontSize: { xs: '2rem', md: '2.5rem' } }}>
          How It Works
        </Typography>

        <Box sx={{ maxWidth: 700, mx: 'auto' }}>
          {[
            {
              title: 'Launch Announcement',
              duration: '7–14 days',
              description: 'Sponsor reveal, prize details, entry requirements, campaign goals',
            },
            {
              title: 'Round 1: Live Auditions',
              duration: 'Day 1',
              description: 'Creators perform or submit. Judges select who advances.',
            },
            {
              title: 'Round 2: Challenge Round',
              duration: 'Day 2',
              description: 'Theme or sponsor integration (optional). Audience engagement drives momentum.',
            },
            {
              title: 'Semi-Finals: Spotlight Stream',
              duration: 'Day 3',
              description: 'Top creators perform; guest judges narrow finalists.',
            },
            {
              title: 'Finals: Championship Stream',
              duration: 'Day 4',
              description: 'Winner selected live + prize awarded + sponsor spotlight segment',
            },
          ].map((step, idx) => (
            <Box
              key={idx}
              sx={{
                display: 'flex',
                gap: 3,
                mb: 4,
                pb: 4,
                borderBottom: idx < 4 ? `1px dashed ${theme.palette.divider}` : 'none',
              }}
            >
              <Box
                sx={{
                  width: 50,
                  height: 50,
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 800,
                  fontSize: '1.2rem',
                  flexShrink: 0,
                }}
              >
                {idx + 1}
              </Box>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {step.title}
                  </Typography>
                  <Chip label={step.duration} size="small" sx={{ fontWeight: 600 }} />
                </Box>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.6 }}>
                  {step.description}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>

        <Typography
          variant="body2"
          sx={{
            textAlign: 'center',
            mt: 4,
            color: theme.palette.primary.main,
            fontWeight: 600,
            fontSize: '1rem',
          }}
        >
          💡 Every round is designed for clips (shareable moments that drive traffic and conversions).
        </Typography>
      </Container>

      {/* Participation Requirements */}
      <Box sx={{ backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)', py: 10 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" sx={{ fontWeight: 800, textAlign: 'center', mb: 3, fontSize: { xs: '2rem', md: '2.5rem' } }}>
            How Participants Qualify
          </Typography>

          <Card
            sx={{
              p: 4,
              mb: 4,
              background: `linear-gradient(135deg, ${theme.palette.primary.main}10, ${theme.palette.secondary.main}10)`,
              border: `2px solid ${theme.palette.primary.main}`,
              borderRadius: 3,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckIcon sx={{ color: theme.palette.primary.main }} />
              Must-Have Requirement
            </Typography>
            <Typography variant="body1" sx={{ fontSize: '1.1rem' }}>
              Active SIKI Pro subscription to enter and compete
            </Typography>
          </Card>

          <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>
            Optional Sponsor Goals (pick 1–3):
          </Typography>

          <Grid container spacing={2}>
            {[
              { emoji: '🛒', text: 'Purchase a sponsor product during the livestream window' },
              { emoji: '⭐', text: 'Subscribe / follow the sponsor\'s channel (or partner creator channel)' },
              { emoji: '💸', text: 'Tip-for-votes (tips count as voting power / engagement signals)' },
              { emoji: '🎟️', text: 'Claim an offer code / email capture (if you want lead gen)' },
              { emoji: '🔁', text: 'Social action: repost highlight clip, tag sponsor, submit proof' },
            ].map((goal, idx) => (
              <Grid item xs={12} sm={6} key={idx}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2 }}>
                  <Typography variant="h5">{goal.emoji}</Typography>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                    {goal.text}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>

          <Typography
            variant="caption"
            sx={{
              display: 'block',
              mt: 4,
              p: 2,
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
              borderRadius: 2,
              fontStyle: 'italic',
              color: theme.palette.text.secondary,
            }}
          >
            <strong>Fairness note:</strong> SIKI can separate "skill judging" from "fan voting" so purchases/tips don't fully determine the winner (keeps credibility high).
          </Typography>
        </Container>
      </Box>

      {/* Judges & Special Guests */}
      <Container maxWidth="lg" sx={{ py: 10 }}>
        <Typography variant="h3" sx={{ fontWeight: 800, textAlign: 'center', mb: 3, fontSize: { xs: '2rem', md: '2.5rem' } }}>
          Hosted with Real Tastemakers
        </Typography>
        <Typography
          variant="body1"
          sx={{
            textAlign: 'center',
            color: theme.palette.text.secondary,
            mb: 6,
            maxWidth: 700,
            mx: 'auto',
            lineHeight: 1.8,
          }}
        >
          Each Power Play includes a host, judges, and special guests who review music live, give feedback, and advance participants through rounds.
        </Typography>

        <Grid container spacing={3}>
          {[
            { icon: <PeopleIcon sx={{ fontSize: 40 }} />, title: 'Host', desc: 'SIKI / sponsor-approved' },
            { icon: <StarsIcon sx={{ fontSize: 40 }} />, title: 'Judges', desc: 'A&Rs, producers, artists, streamers' },
            { icon: <PlayIcon sx={{ fontSize: 40 }} />, title: 'Special Guest Appearances', desc: 'Featured moments + promo segments' },
          ].map((item, idx) => (
            <Grid item xs={12} md={4} key={idx}>
              <Card
                sx={{
                  p: 4,
                  textAlign: 'center',
                  background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'white',
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 2,
                }}
              >
                <Box sx={{ color: theme.palette.primary.main, mb: 2 }}>{item.icon}</Box>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                  {item.title}
                </Typography>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                  {item.desc}
                </Typography>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Sponsor Deliverables */}
      <Box sx={{ backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)', py: 10 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" sx={{ fontWeight: 800, textAlign: 'center', mb: 6, fontSize: { xs: '2rem', md: '2.5rem' } }}>
            Sponsor Deliverables
          </Typography>

          <Grid container spacing={2} sx={{ mb: 5 }}>
            {[
              'Sponsor branding across the Power Play page + stream overlays',
              'On-stream sponsor segment (30–120 seconds) per round',
              'Product placement / call-to-action moments (optional)',
              'Post-round highlight clips delivered for social use (as agreed)',
              'Performance report: entries, attendance, conversions, engagement, top clips',
            ].map((item, idx) => (
              <Grid item xs={12} sm={6} key={idx}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, p: 2 }}>
                  <CheckIcon sx={{ color: theme.palette.primary.main, mt: 0.3 }} />
                  <Typography variant="body1" sx={{ color: theme.palette.text.secondary }}>
                    {item}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>

          <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>
            Optional Add-Ons:
          </Typography>

          <Grid container spacing={2}>
            {[
              'VIP "Judge Seat" (brand rep on panel)',
              'Creator affiliate codes',
              'Dedicated product showcase stream',
              'Winner post-campaign content package',
            ].map((item, idx) => (
              <Grid item xs={12} sm={6} md={3} key={idx}>
                <Chip
                  label={item}
                  sx={{
                    width: '100%',
                    height: 'auto',
                    py: 2,
                    px: 1,
                    '& .MuiChip-label': { whiteSpace: 'normal', textAlign: 'center' },
                    background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                    fontWeight: 600,
                  }}
                />
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Example Power Plays */}
      <Container maxWidth="lg" sx={{ py: 10 }} id="examples">
        <Typography variant="h3" sx={{ fontWeight: 800, textAlign: 'center', mb: 6, fontSize: { xs: '2rem', md: '2.5rem' } }}>
          Example Power Plays
        </Typography>

        <Grid container spacing={4}>
          {examplePowerPlays.map((example, idx) => (
            <Grid item xs={12} md={4} key={idx}>
              <Card
                sx={{
                  p: 4,
                  height: '100%',
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}08, ${theme.palette.secondary.main}08)`,
                  border: `1px solid ${theme.palette.primary.main}40`,
                  borderRadius: 3,
                  transition: 'all 0.3s',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    borderColor: theme.palette.primary.main,
                    boxShadow: `0 16px 40px ${theme.palette.primary.main}30`,
                  },
                }}
              >
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
                  {example.title}
                </Typography>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block', mb: 0.5 }}>
                    PRIZE
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 2 }}>
                    {example.prize}
                  </Typography>

                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block', mb: 0.5 }}>
                    GOALS
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 2 }}>
                    {example.goals}
                  </Typography>

                  <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                    <Chip label={example.rounds} size="small" sx={{ fontWeight: 600 }} />
                    <Chip label={example.reach} size="small" color="primary" sx={{ fontWeight: 600 }} />
                  </Box>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* FAQ */}
      <Box sx={{ backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)', py: 10 }}>
        <Container maxWidth="md">
          <Typography variant="h3" sx={{ fontWeight: 800, textAlign: 'center', mb: 6, fontSize: { xs: '2rem', md: '2.5rem' } }}>
            FAQ
          </Typography>

          {faqs.map((faq, idx) => (
            <Accordion
              key={idx}
              sx={{
                mb: 2,
                background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'white',
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: '8px !important',
                '&:before': { display: 'none' },
                boxShadow: 'none',
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
                  {faq.question}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, lineHeight: 1.8 }}>
                  {faq.answer}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Container>
      </Box>

      {/* Final CTA + Contact */}
      <Container maxWidth="md" sx={{ py: 10 }} id="sponsor-form">
        <Typography variant="h3" sx={{ fontWeight: 800, textAlign: 'center', mb: 3, fontSize: { xs: '2rem', md: '2.5rem' } }}>
          Sponsor the Next Power Play
        </Typography>

        <Card
          sx={{
            p: 5,
            textAlign: 'center',
            background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'white',
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 3,
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
            Interested in sponsoring a Power Play?
          </Typography>
          <Typography variant="body1" sx={{ color: theme.palette.text.secondary, mb: 4, lineHeight: 1.8 }}>
            Reach out to our team to discuss opportunities, prize packages, and campaign goals.
          </Typography>

          <Box
            component="a"
            href="mailto:contact@siki.io"
            sx={{
              display: 'inline-block',
              textDecoration: 'none',
            }}
          >
            <Button
              variant="contained"
              size="large"
              sx={{
                px: 6,
                py: 2,
                fontSize: '1.2rem',
                fontWeight: 700,
                textTransform: 'none',
                borderRadius: 2,
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                boxShadow: `0 8px 24px ${theme.palette.primary.main}40`,
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: `0 12px 32px ${theme.palette.primary.main}60`,
                },
                transition: 'all 0.3s',
              }}
            >
              Contact@siki.io
            </Button>
          </Box>
        </Card>
      </Container>

      {/* Footer CTA */}
      <Box
        sx={{
          py: 8,
          textAlign: 'center',
          background: `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.secondary.main}15 100%)`,
        }}
      >
        <Container maxWidth="sm">
          <BoltIcon sx={{ fontSize: 60, color: theme.palette.primary.main, mb: 2 }} />
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
            Ready to turn creators into customers?
          </Typography>
          <Typography variant="body1" sx={{ color: theme.palette.text.secondary, mb: 3 }}>
            Let's create something viral together.
          </Typography>
          <Button
            variant="outlined"
            onClick={() => history.push('/')}
            sx={{
              px: 4,
              py: 1.5,
              fontWeight: 700,
              textTransform: 'none',
              borderWidth: 2,
              '&:hover': { borderWidth: 2 },
            }}
          >
            Back to Home
          </Button>
        </Container>
      </Box>
    </Box>
  );
}
